import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { useAuth } from './AuthContext';
import { saveUiPreferences } from '../services/settingsAPI';
import { subscribeUiPreferences } from '../services/uiPreferencesBridge';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

const systemPrefersDark = () =>
  typeof window !== 'undefined' &&
  window.matchMedia &&
  window.matchMedia('(prefers-color-scheme: dark)').matches;

export const ThemeProvider = ({ children }) => {
  const { isAuthenticated, AUTH_DEV_BYPASS } = useAuth();
  const [isDark, setIsDark] = useState(systemPrefersDark);
  const skipSaveRef = useRef(true);

  useEffect(() => {
    return subscribeUiPreferences((prefs) => {
      if (prefs.theme === 'dark' || prefs.theme === 'light') {
        skipSaveRef.current = true;
        setIsDark(prefs.theme === 'dark');
      }
    });
  }, []);

  useEffect(() => {
    if (isDark) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [isDark]);

  useEffect(() => {
    if (skipSaveRef.current) {
      skipSaveRef.current = false;
      return;
    }
    if (!isAuthenticated || AUTH_DEV_BYPASS) return;
    saveUiPreferences({ theme: isDark ? 'dark' : 'light' }).catch((err) => {
      console.warn('Failed to save theme preference to D1:', err);
    });
  }, [isDark, isAuthenticated, AUTH_DEV_BYPASS]);

  const toggleTheme = () => {
    setIsDark((prev) => !prev);
  };

  const value = {
    isDark,
    toggleTheme,
    theme: isDark ? 'dark' : 'light',
  };

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};
